import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, of } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import {
  ModalManagerService,
  ConfirmationModalConfig,
} from 'src/app/services/modal-manager.service';
import { take, map } from 'rxjs/operators';
import {
  ConfirmationModalComponent,
  ModalStandardTemplateEnum,
} from '../../../../standalones/components/modals/confirmation-modal/confirmation-modal.component';

export enum FormAction {
  SUBMIT = 'SUBMIT',
  CANCEL = 'CANCEL',
}

export enum FormState {
  Initial = 'Initial', // Form is untouched/unchanged
  ChangedAndValid = 'VALID', // Form has changes and is valid
  ChangedAndInvalid = 'INVALID', // Form has changes and is invalid
  Loading = 'LOADING', // Keep this as we need to show loading state during API calls
  SubmittedAndInvalid = 'SUBMITTED_INVALID', // Form submit clicked but invalid (validate-on-submit mode)
}
@Injectable({
  providedIn: 'root',
})
export class IntegrationsFormService {
  private currentFormState$ = new BehaviorSubject<FormState>(FormState.Initial);
  private formAction$ = new Subject<FormAction>();
  private currentActiveForm?: FormGroup;
  private currentInitialValue?: any;
  private subscriptions: Subscription[] = [];

  constructor(private dialog: MatDialog, private modalManagerService: ModalManagerService) {}

  registerForm(form: FormGroup, initialValue: any): void {
    // Clean up any existing subscriptions before registering new ones
    this.cleanup();

    this.currentActiveForm = form;
    this.currentInitialValue = initialValue;

    // Store subscriptions for cleanup
    this.subscriptions.push(
      form.valueChanges.subscribe(() => this.updateFormState()),
      form.statusChanges.subscribe(() => this.updateFormState()),
    );

    this.updateFormState();
  }

  /**
   * IMPORTANT: Only the parent component (UserSettingsIntegrationsComponent) should call cleanup().
   * Child components (like webhooks) should not clean up on destroy as this would break subscriptions
   * when switching tabs. If a child component calls cleanup and then another form is registered,
   * the service would lose track of previous subscriptions, leading to memory leaks.
   */
  public cleanup(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    // Clear form references
    this.currentActiveForm = undefined;
    this.currentInitialValue = undefined;
  }

  updateInitialValue(value: any): void {
    this.currentInitialValue = value;
    this.updateFormState();
  }

  getFormState$(): Observable<FormState> {
    return this.currentFormState$.asObservable();
  }

  getFormAction$(): Observable<FormAction> {
    return this.formAction$.asObservable();
  }

  private updateFormState(): void {
    if (!this.currentActiveForm || !this.currentInitialValue) {
      this.currentFormState$.next(FormState.Initial);
      return;
    }

    const isChanged = !isEqual(this.currentInitialValue, this.currentActiveForm.getRawValue());

    if (!isChanged) {
      this.currentFormState$.next(FormState.Initial);
      return;
    }

    // Default (OnChange)
    this.currentFormState$.next(
      this.currentActiveForm.valid ? FormState.ChangedAndValid : FormState.ChangedAndInvalid,
    );
  }

  setLoading(loading: boolean): void {
    if (!this.currentActiveForm) {
      return;
    }

    if (loading) {
      this.currentActiveForm.disable();
      this.currentFormState$.next(FormState.Loading);
    } else {
      this.currentActiveForm.enable();
      this.updateFormState();
    }
  }

  /**
   * Resets the active form to its initial state
   */
  resetForm(): void {
    if (!this.currentActiveForm || !this.currentInitialValue) {
      return;
    }

    this.currentActiveForm.patchValue(this.currentInitialValue);
    this.currentActiveForm.markAsPristine();
    this.currentFormState$.next(FormState.Initial);
  }

  dispatchAction(action: FormAction): void {
    this.formAction$.next(action);

    if (action === FormAction.CANCEL) {
      this.resetForm();
    } else if (action === FormAction.SUBMIT) {
      // Set loading state to true for SUBMIT actions
      this.setLoading(true);
    }
  }

  /**
   * This functin is used in case of new form validation behavior which enabled the "Confirm" button
   * in the footer until the user clicks it then we run validation. if valid then submit else disable the button
   * This is different behavior than the old where we disable the "Confirm" button as long as the form is invalid.
   *
   * Submit flow: validate; optional confirmation; then dispatch and report boolean.
   * Returns true when submission proceeds (Loading state emitted); false when invalid.
   */
  handleSubmit(confirmationConfig?: ConfirmationModalConfig): Observable<boolean> {
    // Invalid forms cannot proceed
    if (!this.currentActiveForm?.valid) {
      this.currentActiveForm?.markAllAsTouched();
      this.currentFormState$.next(FormState.SubmittedAndInvalid);
      return of(false);
    }

    if (!confirmationConfig) {
      this.performSubmit();
      return of(true);
    }

    return this.modalManagerService.showConfirmationModal(confirmationConfig).pipe(
      take(1),
      map((ok) => {
        if (ok) {
          this.performSubmit();
          return true;
        }
        return false;
      }),
    );
  }

  /** Sets loading and emits SUBMIT action. */
  private performSubmit(): void {
    this.setLoading(true);
    this.formAction$.next(FormAction.SUBMIT);
  }

  hasUnsavedChanges(): boolean {
    return !!this.currentActiveForm && this.currentFormState$.value !== FormState.Initial;
  }

  /**
   * Check for unsaved changes and show confirmation dialog if needed
   * @returns Observable that resolves to true if action should proceed, false if cancelled
   */
  confirmIfUnsavedChanges(): Observable<boolean> {
    if (this.hasUnsavedChanges()) {
      return this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            type: ModalStandardTemplateEnum.UNSAVED_CHANGES,
          },
        })
        .afterClosed()
        .pipe(
          take(1),
          map((result) => result === 'confirm'),
        );
    }

    // No unsaved changes, proceed immediately
    return of(true);
  }
}
